import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import { getMmpSettingsApi } from '#/api/desk-api';
import LoadingIndicator from '#/components/loading-indicator/loading-indicator';
import PageSubTitle from '#/components/page-sub-title/page-sub-title';
import EditMmpSettings from '#/pages/settings/components/edit-mmp-settings';

const useStyles = makeStyles({
  left: {
    width: 230,
  },
});

export default function SettingsGrfqMdrfq() {
  const DEFAULT_MMP_SETTINGS = {
    mmp_reset_interval_seconds: '',
    execution_limit: '',
    execution_window: '',
    volume_threshold: '',
    volume_window: '',
  };

  const classes = useStyles();
  const [settings, setSettings] = useState(DEFAULT_MMP_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchMmpSettings = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await getMmpSettingsApi();
      const newSettings = {
        mmp_reset_interval_seconds: response.mmp_reset_interval_seconds || '',
        execution_limit: response.execution_limit || '',
        execution_window: response.execution_window || '',
        volume_threshold: response.volume_threshold || '',
        volume_window: response.volume_window || '',
      };
      setSettings(newSettings);
    } catch (err) {
      console.error('Error fetching MMP settings:', err);
      setError('Failed to load settings.');
      setSettings(DEFAULT_MMP_SETTINGS);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchMmpSettings();
  }, []);

  const handleSettingsUpdate = () => {
    fetchMmpSettings(); // Refetch settings after update
  };

  if (isLoading) {
    return (
      <LoadingIndicatorContainer>
        <LoadingIndicator />
      </LoadingIndicatorContainer>
    );
  }

  if (error && !settings.mmp_reset_interval_seconds) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Grid
      container
      alignItems="center"
      spacing={4}
      data-testid="mmp-settings-page"
    >
      <Grid item container direction="column" spacing={3}>
        <Grid item container alignItems="center">
          <Grid item className={classes.left}>
            <PageSubTitle text="MARKET MAKER PROTECTION" />
          </Grid>
          <Grid item xs>
            <EditMmpSettings
              settings={settings}
              onSettingsUpdate={handleSettingsUpdate}
            >
              <Link underline="none" variant="body1" color="textSecondary">
                Edit
              </Link>
            </EditMmpSettings>
          </Grid>
        </Grid>

        <Grid item>
          <SubTitle>Execution Count MMP</SubTitle>
          <Value>
            <Typography color="textSecondary">Time Period</Typography>
            <Typography>
              {settings.execution_window || 'Not set'}{' '}
              {settings.execution_window && 'Seconds'}
            </Typography>
          </Value>
          <Value>
            <Typography color="textSecondary">
              Maximum # of executions allowed
            </Typography>
            <Typography>{settings.execution_limit || 'Not set'}</Typography>
          </Value>
        </Grid>

        <Grid item>
          <SubTitle custom>Notional Volume MMP</SubTitle>
          <Value>
            <Typography color="textSecondary">Volume Allowed</Typography>
            <Typography>{settings.volume_threshold || 'Not set'}</Typography>
          </Value>
          <Value>
            <Typography color="textSecondary">Volume Time Period</Typography>
            <Typography>
              {settings.volume_window || 'Not set'}{' '}
              {settings.volume_window && 'Seconds'}
            </Typography>
          </Value>
        </Grid>

        <Grid item>
          <SubTitle custom>General MMP Settings</SubTitle>
          <Value>
            <Typography color="textSecondary">MMP Reset Time</Typography>
            <Typography>
              {settings.mmp_reset_interval_seconds || 'Not set'}{' '}
              {settings.mmp_reset_interval_seconds && 'Seconds'}
            </Typography>
          </Value>
        </Grid>
      </Grid>
    </Grid>
  );
}

const SubTitle = styled(Typography)`
  margin-bottom: 15px;
  font-size: 0.75rem;
  ${({ custom }) => custom && 'margin-top: 20px;'}
`;

const Value = styled.div`
  display: grid;
  grid-template-columns: 230px auto;
  margin-bottom: 10px;
  align-items: center;
`;

const LoadingIndicatorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
`;
